import React, { useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "../shell/PublicLayout";
import Overline from "../components/Overline";
import Button from "../components/Button";
import { TextInput, Field } from "../components/Input";
import { UserSubmitQuestion } from "../../service/service";
import { paths } from "../routing/paths";

/*
 * /ask-question — submit a question for Rabbi Paltiel's Q&A, in the
 * redesign: breadcrumbs, title block, one form card (name + email
 * optional, question required), inline success and error states — no
 * modal popups. Same user/ask-question endpoint as before.
 */

const textareaClasses =
  "font-sans text-[16px] leading-6 font-normal px-4 py-3 w-full box-border border rounded-xl min-h-[180px] resize-y " +
  "bg-white text-ic-navy placeholder:text-ic-placeholder transition-all duration-150 ease-out " +
  "focus:outline-none focus:border-ic-blue focus:shadow-ic-focus-soft disabled:bg-ic-disabledBg disabled:cursor-not-allowed";

const EMPTY = { name: "", email: "", question: "" };

export default function AskQuestionPage() {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const [touched, setTouched] = useState(false);

  const questionMissing = form.question.trim().length === 0;
  const busy = status.state === "sending";

  const update = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (questionMissing || busy) return;
    setStatus({ state: "sending", message: "" });
    try {
      const result = await UserSubmitQuestion({
        name: form.name.trim(),
        email: form.email.trim(),
        question: form.question.trim(),
      });
      if (result?.status) {
        setStatus({ state: "sent", message: result.message || "" });
        setForm(EMPTY);
        setTouched(false);
      } else {
        setStatus({ state: "error", message: result?.message || "We couldn't send your question. Please try again." });
      }
    } catch (err) {
      setStatus({ state: "error", message: typeof err === "string" ? err : "We couldn't send your question. Please try again." });
    }
  };

  return (
    <PublicLayout>
      <main className="max-w-[1280px] w-full mx-auto px-5 pt-5 pb-10 flex flex-col gap-5">
        <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-[14px] leading-5 whitespace-nowrap">
          <Link to={paths.home()} className="text-ic-blue font-semibold py-3 px-1 -my-3 inline-flex items-center min-h-[44px] hover:underline">
            Home
          </Link>
          <span className="text-ic-slate">→</span>
          <Link to={paths.permalink("qa")} className="text-ic-blue font-semibold py-3 px-1 -my-3 inline-flex items-center min-h-[44px] hover:underline">
            Q&A
          </Link>
          <span className="text-ic-slate">→</span>
          <span className="text-ic-body font-semibold px-1">Ask a question</span>
        </nav>

        <section className="flex flex-col gap-2">
          <Overline>Q&A</Overline>
          <h1 className="m-0 font-extrabold text-black tracking-[-0.5px]" style={{ fontSize: "clamp(26px, 6vw, 40px)", lineHeight: 1.2 }}>
            Ask Rabbi Paltiel a question
          </h1>
        </section>

        <section className="flex flex-col gap-4 max-w-[720px] w-full">
          {status.state === "sent" ? (
            <div role="status" className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised p-6 flex flex-col gap-4">
              <div className="w-12 h-12 rounded-full bg-[rgba(36,154,26,0.1)] text-ic-success flex items-center justify-center">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12.5l4.5 4.5L19 7.5" />
                </svg>
              </div>
              <h2 className="m-0 text-[24px] font-bold leading-8 tracking-[-0.3px] text-black">Your question is in</h2>
              <p className="m-0 text-[18px] leading-7 text-ic-body">
                {status.message || "Thank you — it's been passed along to Rabbi Paltiel."}
              </p>
              <div className="flex gap-3 flex-wrap">
                <Button size="lg" onClick={() => setStatus({ state: "idle", message: "" })}>
                  Ask another question
                </Button>
                <Link
                  to={paths.permalink("qa")}
                  className="inline-flex items-center h-12 px-5 text-[16px] font-semibold text-ic-body border border-ic-borderGray rounded-3xl no-underline transition-all duration-150 ease-out hover:border-ic-blue hover:text-ic-blue hover:no-underline"
                >
                  Browse the Q&A archive →
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} noValidate className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised p-6 flex flex-col gap-5">
              <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))" }}>
                <Field label="Full name (optional)" htmlFor="ask-name">
                  <TextInput id="ask-name" name="name" type="text" autoComplete="name" value={form.name} onChange={update} disabled={busy} />
                </Field>
                <Field label="Email (optional)" htmlFor="ask-email">
                  <TextInput id="ask-email" name="email" type="email" autoComplete="email" inputMode="email" value={form.email} onChange={update} disabled={busy} />
                </Field>
              </div>
              <Field
                label="Your question"
                htmlFor="ask-question"
                required
                error={touched && questionMissing ? "Please write your question before sending." : undefined}
              >
                <textarea
                  id="ask-question"
                  name="question"
                  required
                  aria-required="true"
                  aria-invalid={touched && questionMissing}
                  placeholder="What would you like to ask?"
                  value={form.question}
                  onChange={update}
                  disabled={busy}
                  className={`${textareaClasses} ${touched && questionMissing ? "border-ic-error shadow-ic-error-ring" : "border-ic-lightGray"}`}
                />
              </Field>
              {status.state === "error" && (
                <p role="alert" className="m-0 text-[14px] leading-5 text-ic-error">
                  {status.message}
                </p>
              )}
              <div className="flex items-center gap-4 flex-wrap">
                <Button type="submit" size="lg" disabled={busy}>
                  {busy ? "Sending…" : "Send my question"}
                </Button>
                <span className="text-[14px] leading-5 text-ic-slate">We never share your email.</span>
              </div>
            </form>
          )}

          {status.state !== "sent" && (
            <Link
              to={paths.permalink("qa")}
              className="self-start text-ic-blue text-[16px] font-semibold underline inline-flex items-center min-h-[44px] hover:text-ic-blueHover"
            >
              Browse the Q&A archive →
            </Link>
          )}
        </section>
      </main>
    </PublicLayout>
  );
}
